import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AppLoading } from '@/components/AppLoading';
import { logger } from '@/lib/logger';

import type { PropsWithChildren, ReactElement } from 'react';

export interface AuthGuardProps extends PropsWithChildren {
  /** Where to redirect unauthenticated visitors. Defaults to `/signin`. */
  redirectTo?: string;
  /** What to render while Auth0 is still resolving the session. */
  fallback?: ReactElement;
}

/**
 * Route guard: renders children only for authenticated users. While Auth0
 * is still hydrating it renders the fallback (or a minimal placeholder); on
 * a confirmed-unauthenticated state it redirects to the sign-in route and
 * passes the current location in router state so the post-sign-in handler
 * can return the user to where they came from.
 */
export function AuthGuard({
  children,
  redirectTo = '/signin',
  fallback,
}: AuthGuardProps): ReactElement | null {
  const { isAuthenticated, isLoading, error } = useAuth0();
  const location = useLocation();

  useEffect(() => {
    if (error) {
      logger.error('[auth] Auth0 error', { error });
    }
  }, [error]);

  if (isLoading) {
    // AF-474: a branded, announced loading state — no blank white flash.
    return fallback ?? <AppLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
